import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
   AbstractControl,
   FormArray,
   FormBuilder,
   FormGroup,
   FormsModule,
   ReactiveFormsModule,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { CacheService } from '@api/custom-services/cache.service';
import { UserService } from '@api/custom-services/user.service';
import { Authority, CompanySettingsDto, DestinationDto, FileType, UpdateCompanySettingsDto } from '@api/models';
import {
   CashRegisterService,
   CompanyService,
   ManageService,
   PayoffService,
   StatisticsService,
   TicketsService,
} from '@api/services';
import { environment } from '@env/environment';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { InputFieldComponent } from '@shared/components/input-field/input-field.component';
import { SelectField, SelectFieldComponent } from '@shared/components/select-field/select-field.component';
import { SelectFileFieldComponent } from '@shared/components/select-file-field/select-file-field.component';
import { PaymentProvider } from '@shared/constants/enum';
import { HasAuthoritiesDirective } from '@shared/directives/has-authorities.directive';
import { SubscriptionDirective } from '@shared/directives/subscriptions.directive';
import { Observable, tap } from 'rxjs';
import { toSelectField } from 'src/utils/mappers';

@Component({
   selector: 'app-company-settings',
   imports: [
      CommonModule,
      FormsModule,
      MatButtonModule,
      SelectFileFieldComponent,
      MatCardModule,
      InputFieldComponent,
      SelectFieldComponent,
      MatFormFieldModule,
      MatIconModule,
      MatCheckboxModule,
      ReactiveFormsModule,
      HasAuthoritiesDirective,
   ],
   templateUrl: './company-settings.component.html',
   styleUrl: './company-settings.component.scss',
})
export class CompanySettingsComponent extends SubscriptionDirective {
   private dialog = inject(MatDialog);
   private route = inject(ActivatedRoute);

   settings$!: Observable<CompanySettingsDto>;
   readonly PaymentProvider = PaymentProvider;
   readonly fileType = FileType;
   showHideButtons = false;

   paymentProviderOptions = [
      {
         name: 'None',
         id: PaymentProvider.NONE,
      },
      {
         name: 'Sumup',
         id: PaymentProvider.SUMUP,
      },
      {
         name: 'Mollie',
         id: PaymentProvider.MOLLIE,
      },
   ];

   destinationOptions: SelectField[] = [];

   form = this.formBuilder.nonNullable.group({
      logoId: [undefined as any as number],
      incognitoLogoId: [undefined as any as number],
      imageId: [undefined as any as number],
      incognitoImageId: [undefined as any as number],
      merchantCode: [undefined as any as string],
      mollieApiKey: [undefined as any as string],
      sumUpClientId: [undefined as any as string],
      sumUpClientSecret: [undefined as any as string],
      sumUpApiKey: [undefined as any as string],
      creditCardFee: [undefined as any as number, [Validators.required, Validators.min(0), Validators.max(20)]],
      allowDepositPOS: [undefined as any as boolean],
      webTicketsApproval: [undefined as any as boolean],
      paidFullLabel: [undefined as any as string],
      paymentProvider: [undefined as any as number, Validators.required],
      destinationId: [undefined as any as number],
      paymentDepositExpireAfter: [undefined as any as number],
      paymentExpireAfter: [undefined as any as number],
      ticketEditHours: [undefined as any as number],
      ghostExpiredTicketsAfterMinutes: [undefined as any as number],
      ticketSettings: this.formBuilder.nonNullable.group({
         lastName: this.createSettingItem(),
         bottle: this.createSettingItem(false),
         email: this.createSettingItem(),
         customerNationalityId: this.createSettingItem(),
         preferredPaymentLink: this.createSettingItem(),
         hotelId: this.createSettingItem(),
         phone: this.createSettingItem(),
         alternativePhone: this.createSettingItem(),
         pickupLocationId: this.createSettingItem(),
         residenceId: this.createSettingItem(),
         saleLocationId: this.createSettingItem(),
         tourOperatorId: this.createSettingItem(),
         message: this.createSettingItem(),
         arrivalDate: this.createSettingItem(),
         plannedPickupDate: this.createSettingItem(),
         departureDate: this.createSettingItem(),
         membershipEventId: this.createSettingItem(),
      }),
      splitPromoters: [false],
      hidePromoterRanking: [false],
      companyName: [undefined as any as string],
      address: [undefined as any as string],
      vatNumber: [undefined as any as string],
      invoiceEnabled: [false],
      invoiceNumberPrefix: [undefined as any as string],
      invoiceFooterNotes: [undefined as any as string],
      invoiceLabels: this.formBuilder.nonNullable.group({
         title: [undefined as any as string],
         number: [undefined as any as string],
         date: [undefined as any as string],
         description: [undefined as any as string],
         amount: [undefined as any as string],
         totalOnline: [undefined as any as string],
         totalOffline: [undefined as any as string],
         eventOrganizer: [undefined as any as string],
      }),
      onlineFees: this.formBuilder.array([]),
      invoicePackages: this.formBuilder.array([]),
      payoffExcludeZeroAmount: [undefined as any as boolean],
   });

   get paymentProvider(): number | undefined {
      return this.form.get('paymentProvider')?.value;
   }

   constructor(
      private cacheService: CacheService,
      private companyService: CompanyService,
      private cashRegisterService: CashRegisterService,
      private payoffService: PayoffService,
      private statisticsService: StatisticsService,
      private ticketsService: TicketsService,
      private manageService: ManageService,
      private formBuilder: FormBuilder,
      private userService: UserService,
      private _snackBar: MatSnackBar
   ) {
      super();
      this.initEdit();
      this.checkShowClearButtons();
      this.loadDestinations();
   }

   private loadDestinations() {
      this.subs = this.manageService.destinationsControllerList().subscribe((list: DestinationDto[]) => {
         this.destinationOptions = list.map(toSelectField);
      });
   }

   private checkShowClearButtons() {
      this.subs = this.route.queryParams.subscribe((params) => {
         this.showHideButtons = params['showHideButtons'] === 'true';
      });
   }

   createSettingItem(includeRequired: boolean = true): any {
      return this.formBuilder.nonNullable.group(
         {
            hidden: [false],
            ...(includeRequired && { required: [false] }),
         },
         { validators: this.addSettingItemValidator() }
      );
   }

   addSettingItemValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const value = control.value;
         return value.required && value.hidden ? { invalid: true } : null;
      };
   }

   initEdit() {
      this.settings$ = this.companyService.companyControllerGetCompanySettings().pipe(
         tap((settings) => {
            this.form.patchValue(settings);

            if (settings.onlineFees) {
               settings.onlineFees.forEach((item) => {
                  this.onlineFees.push(this.buildOnlineFeeForm(item.minPrice, item.maxPrice, item.percentage));
               });
            }

            if (settings.invoicePackages) {
               settings.invoicePackages.forEach((item) => {
                  this.invoicePackages.push(this.buildInvoicePackageForm(item.minPrice, item.maxPrice, item.name));
               });
            }
         })
      );
   }

   clearCashRegister(): void {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
         width: '350px',
         data: {
            title: 'Confirmation',
            message: `Are you sure you want to clear the Cash Register?`,
         },
      });

      dialogRef.afterClosed().subscribe((result) => {
         if (result) {
            this.subs = this.cashRegisterService
               .cashRegisterControllerClear()
               .subscribe(() => this._snackBar.open('Cash Register was cleared.', 'OK'));
         }
      });
   }

   clearPayoff(): void {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
         width: '350px',
         data: {
            title: 'Confirmation',
            message: `Are you sure you want to clear the Payoff?`,
         },
      });

      dialogRef.afterClosed().subscribe((result) => {
         if (result) {
            this.subs = this.payoffService
               .payoffControllerClear()
               .subscribe(() => this._snackBar.open('Payoff was cleared.', 'OK'));
         }
      });
   }

   recalculatePayoff(): void {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
         width: '350px',
         data: {
            title: 'Confirmation',
            message: `Are you sure you want to recalculate the Payoff?`,
         },
      });

      dialogRef.afterClosed().subscribe((result) => {
         if (result) {
            this.subs = this.payoffService
               .payoffControllerRecalculatePayoff()
               .subscribe(() => this._snackBar.open('Payoff was recalculated.', 'OK'));
         }
      });
   }

   updateAllStatistics(): void {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
         width: '350px',
         data: {
            title: 'Confirmation',
            message: `Are you sure you want to update all companies statistics?`,
         },
      });

      dialogRef.afterClosed().subscribe((result) => {
         if (result) {
            this.subs = this.statisticsService
               .statisticsControllerUpdateStats()
               .subscribe(() => this._snackBar.open('Statistics are updated.', 'OK'));
         }
      });
   }

   resendSaleNotifications(): void {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
         width: '350px',
         data: {
            title: 'Confirmation',
            message: `Are you sure you want to resend all sale notifications for tickets?`,
         },
      });

      dialogRef.afterClosed().subscribe((result) => {
         if (result) {
            this.subs = this.ticketsService
               .ticketsControllerResendSaleNotifications({ body: { }})
               .subscribe(() => this._snackBar.open('Notifications are sent successfully.', 'OK'));
         }
      });
   }

   /** Recursively collect the paths of invalid controls so the user knows what blocks the save. */
   private getInvalidControls(control: AbstractControl, prefix = ''): string[] {
      if (control instanceof FormGroup || control instanceof FormArray) {
         return Object.entries(control.controls).flatMap(([key, child]) =>
            this.getInvalidControls(child as AbstractControl, prefix ? `${prefix}.${key}` : key)
         );
      }
      return control.invalid ? [prefix] : [];
   }

   saveSettings() {
      if (this.form.invalid) {
         this.form.markAllAsTouched();
         const invalid = this.getInvalidControls(this.form);
         this._snackBar.open(`Please complete: ${invalid.join(', ')}`, 'OK');
         return;
      }

      const request$ = this.companyService.companyControllerUpdateMyCompanySettings({
         body: this.form.value as UpdateCompanySettingsDto,
      });

      this.subs = request$.subscribe({
         next: () => {
            this.userService.refreshToken();
            this._snackBar.open('Settings was successfully saved.', 'OK');
         },
         complete: () => {
            this.cacheService.refresh$.company.next();
         },
      });
   }

   buildOnlineFeeForm(minPrice?: number, maxPrice?: number, percentage?: number) {
      return this.formBuilder.nonNullable.group({
         minPrice: [minPrice, Validators.required],
         maxPrice: [maxPrice, Validators.required],
         percentage: [percentage, [Validators.required, Validators.min(0), Validators.max(20)]],
      });
   }

   get onlineFees() {
      return this.form.controls['onlineFees'] as FormArray;
   }

   addOnlineFee() {
      this.onlineFees.push(this.buildOnlineFeeForm());
   }

   removeOnlineFee(index: number) {
      this.onlineFees.removeAt(index);
   }

   buildInvoicePackageForm(minPrice?: number, maxPrice?: number, name?: string) {
      return this.formBuilder.nonNullable.group({
         minPrice: [minPrice, Validators.required],
         maxPrice: [maxPrice, Validators.required],
         name: [name, Validators.required],
      });
   }

   get invoicePackages() {
      return this.form.controls['invoicePackages'] as FormArray;
   }

   addInvoicePackage() {
      this.invoicePackages.push(this.buildInvoicePackageForm());
   }

   removeInvoicePackage(index: number) {
      this.invoicePackages.removeAt(index);
   }

   protected readonly environment = environment;
   protected readonly Authority = Authority;
}
